import { useState } from "react";
import { drybulkCargoOperationsService } from "@services/drybulkCargoOperationsApi";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import {
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
  GRID_REFETCH_INTERVAL_MS,
  OPERATION_LOG_PAGE_SIZE,
} from "../constants";
import { usePollingGate } from "./usePollingGate";

const OPERATION_LOG_INQUIRY_ID = "drybulk_cargo_operations.operation_log.page";

export const useOperationLog = (operationId: string, status: string | null | undefined) => {
  const { busy } = usePollingGate();
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.operationLog(operationId, page, status),
    queryFn: () =>
      drybulkCargoOperationsService.getOperationLog(operationId, page - 1, OPERATION_LOG_PAGE_SIZE),
    enabled: Boolean(operationId),
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchInterval: busy ? false : GRID_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });
  useTrackPendingInquiry(OPERATION_LOG_INQUIRY_ID, {
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    dataUpdatedAt: query.dataUpdatedAt,
  });

  const changePage = (nextPage: number) => {
    if (nextPage !== page) {
      startInquiryTiming(OPERATION_LOG_INQUIRY_ID, {
        module: DRYBULK_CARGO_OPS_TELEMETRY.module,
        screen: DRYBULK_CARGO_OPS_TELEMETRY.operationLogScreen,
        action: "page_operation_log",
        entityType: "cargo_operation",
      });
    }
    setPage(nextPage);
  };

  return {
    log: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    page,
    changePage,
  };
};
